// AnnouncementCard.jsx
// Full announcement card: header, body, attachment, reactions, comments.
import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  MoreHorizontal, Pin, MessageSquare, Smile, Trash2, Download,
  FileText, Image as ImageIcon, EyeOff, Lock, Unlock, Send,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON  = "#00FF99";
const AMBER = "#EF9F27";
const BLUE  = "#378ADD";

const EMOJI_PALETTE = ["👍", "❤️", "😂", "😮", "🎉", "🙏", "🔥", "💯", "👏", "😢"];

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const initials = (name) =>
  (name || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const relTime = (iso) => {
  if (!iso) return "";
  const diff = (new Date() - new Date(iso)) / 1000;
  if (diff < 60)    return "Just now";
  if (diff < 3600)  return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.round(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const isImageType = (mime, name) => {
  if (mime && mime.startsWith("image/")) return true;
  if (!name) return false;
  return /\.(png|jpg|jpeg|gif|webp)$/i.test(name);
};

const absoluteUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API_BASE}${url}`;
};

/* ─────────────────────────────────────────────────────────
   EmojiPicker — small panel of clickable emojis
───────────────────────────────────────────────────────── */
function EmojiPicker({ onPick, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        bottom: "calc(100% + 6px)",
        left: 0,
        background: "#1a1a1a",
        border: "0.5px solid #333",
        borderRadius: 12,
        padding: 8,
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        gap: 4,
        zIndex: 100,
        boxShadow: "0 4px 20px rgba(0,0,0,0.6)",
      }}
    >
      {EMOJI_PALETTE.map((e) => (
        <button
          key={e}
          onClick={() => { onPick(e); onClose(); }}
          style={{
            fontSize: 20,
            padding: 6,
            borderRadius: 8,
            cursor: "pointer",
            background: "transparent",
            border: "none",
            color: "inherit",
            lineHeight: 1,
          }}
          onMouseEnter={(ev) => ev.currentTarget.style.background = "rgba(255,255,255,0.08)"}
          onMouseLeave={(ev) => ev.currentTarget.style.background = "transparent"}
        >
          {e}
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   ReactionBar — emoji buttons + count
───────────────────────────────────────────────────────── */
function ReactionBar({ counts, userReaction, onToggle, disabled, compact }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const entries = Object.entries(counts || {}).filter(([, n]) => n > 0);

  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", position: "relative" }}>
      {entries.map(([emoji, count]) => {
        const selected = userReaction === emoji;
        return (
          <button
            key={emoji}
            onClick={() => !disabled && onToggle(emoji)}
            disabled={disabled}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: compact ? "2px 7px" : "4px 9px",
              borderRadius: 999,
              border: selected ? `1px solid ${NEON}` : "0.5px solid #2a2a2a",
              background: selected ? "rgba(0,255,153,0.12)" : "rgba(255,255,255,0.03)",
              color: selected ? NEON : "#bbb",
              fontSize: compact ? 11 : 12,
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "all .15s",
            }}
          >
            <span style={{ fontSize: compact ? 12 : 14 }}>{emoji}</span>
            <span>{count}</span>
          </button>
        );
      })}

      {!disabled && (
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setPickerOpen((o) => !o)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: compact ? "2px 7px" : "4px 9px",
              borderRadius: 999,
              border: "0.5px dashed #333",
              background: "transparent",
              color: "#888",
              fontSize: compact ? 11 : 12,
              cursor: "pointer",
            }}
          >
            <Smile size={compact ? 11 : 13} />
            {entries.length === 0 && <span>React</span>}
          </button>
          {pickerOpen && <EmojiPicker onPick={onToggle} onClose={() => setPickerOpen(false)} />}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   AdminMenu — moderation controls
───────────────────────────────────────────────────────── */
function AdminMenu({ ann, onPatch, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const item = {
    padding: "8px 12px",
    fontSize: 12,
    color: "#ddd",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "transparent",
    border: "none",
    width: "100%",
    textAlign: "left",
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Admin controls"
        style={{
          background: "transparent",
          border: "0.5px solid #2a2a2a",
          borderRadius: 6,
          padding: 4,
          color: "#888",
          cursor: "pointer",
        }}
      >
        <MoreHorizontal size={14} />
      </button>
      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 4px)",
          right: 0,
          background: "#151515",
          border: "0.5px solid #2a2a2a",
          borderRadius: 8,
          minWidth: 200,
          zIndex: 50,
          padding: "4px 0",
          boxShadow: "0 4px 18px rgba(0,0,0,0.6)",
        }}>
          <button style={item} onClick={() => { onPatch({ pinned: !ann.pinned }); setOpen(false); }}>
            <Pin size={12} /> {ann.pinned ? "Unpin" : "Pin"}
          </button>
          <button style={item} onClick={() => { onPatch({ allow_comments: !ann.allow_comments }); setOpen(false); }}>
            {ann.allow_comments ? <Lock size={12} /> : <Unlock size={12} />}
            {ann.allow_comments ? "Disable comments" : "Enable comments"}
          </button>
          <button style={item} onClick={() => { onPatch({ allow_reactions: !ann.allow_reactions }); setOpen(false); }}>
            {ann.allow_reactions ? <Lock size={12} /> : <Unlock size={12} />}
            {ann.allow_reactions ? "Disable reactions" : "Enable reactions"}
          </button>
          <button style={item} onClick={() => { onPatch({ status: ann.status === "hidden" ? "published" : "hidden" }); setOpen(false); }}>
            <EyeOff size={12} /> {ann.status === "hidden" ? "Unhide" : "Hide"}
          </button>
          <div style={{ borderTop: "0.5px solid #2a2a2a", margin: "4px 0" }} />
          <button
            style={{ ...item, color: "#e66" }}
            onClick={() => { if (window.confirm("Delete this announcement?")) { onDelete(); setOpen(false); } }}
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   CommentItem
───────────────────────────────────────────────────────── */
function CommentItem({ comment, onReact, onDelete }) {
  return (
    <div style={{ display: "flex", gap: 10, padding: "8px 0", borderTop: "0.5px solid #1a1a1a" }}>
      <div style={{
        width: 26, height: 26, borderRadius: "50%",
        background: "rgba(55,138,221,0.12)", color: BLUE,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 10, fontWeight: 600, flexShrink: 0,
      }}>
        {initials(comment.author_name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, marginBottom: 2 }}>
          <span style={{ color: "#ddd", fontWeight: 500 }}>{comment.author_name}</span>
          <span style={{ color: "#555" }}>·</span>
          <span style={{ color: "#666" }}>{relTime(comment.created_at)}</span>
          {comment.can_delete && (
            <button
              onClick={onDelete}
              title="Delete"
              style={{
                marginLeft: "auto", background: "transparent", border: "none",
                color: "#555", cursor: "pointer", padding: 2,
              }}
            >
              <Trash2 size={11} />
            </button>
          )}
        </div>
        <p style={{ fontSize: 12, color: "#bbb", lineHeight: 1.5, margin: "0 0 6px 0" }}>
          {comment.body}
        </p>
        <ReactionBar
          counts={comment.reaction_counts}
          userReaction={comment.user_reaction}
          onToggle={onReact}
          compact
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   AnnouncementCard
───────────────────────────────────────────────────────── */
export default function AnnouncementCard({ announcement: initialAnn, onChanged, onDeleted }) {
  const [ann, setAnn] = useState(initialAnn);
  useEffect(() => { setAnn(initialAnn); }, [initialAnn]);

  const [commentsOpen, setCommentsOpen]   = useState(false);
  const [comments,     setComments]       = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentText,  setCommentText]    = useState("");
  const [sendingComment, setSendingComment] = useState(false);

  const loadComments = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/announcements/${ann.id}/comments`, { headers: authHeaders() });
      if (res.ok) {
        const d = await res.json();
        setComments(Array.isArray(d) ? d : []);
        setCommentsLoaded(true);
      }
    } catch {}
  }, [ann.id]);

  const toggleComments = async () => {
    const next = !commentsOpen;
    setCommentsOpen(next);
    if (next && !commentsLoaded) await loadComments();
  };

  const reactToAnn = async (emoji) => {
    try {
      const res = await fetch(`${API_BASE}/api/announcements/${ann.id}/react`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ emoji }),
      });
      if (!res.ok) return;
      const counts = { ...(ann.reaction_counts || {}) };
      const prev   = ann.user_reaction;
      // Remove prior selection
      if (prev) counts[prev] = Math.max(0, (counts[prev] || 0) - 1);
      let userReaction = null;
      if (prev === emoji) {
        // toggled off
      } else {
        counts[emoji] = (counts[emoji] || 0) + 1;
        userReaction = emoji;
      }
      setAnn({ ...ann, reaction_counts: counts, user_reaction: userReaction });
    } catch {}
  };

  const reactToComment = async (comment, emoji) => {
    try {
      const res = await fetch(`${API_BASE}/api/announcements/comments/${comment.id}/react`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ emoji }),
      });
      if (!res.ok) return;
      setComments((cs) => cs.map((c) => {
        if (c.id !== comment.id) return c;
        const counts = { ...(c.reaction_counts || {}) };
        const prev = c.user_reaction;
        if (prev) counts[prev] = Math.max(0, (counts[prev] || 0) - 1);
        let userReaction = null;
        if (prev !== emoji) {
          counts[emoji] = (counts[emoji] || 0) + 1;
          userReaction = emoji;
        }
        return { ...c, reaction_counts: counts, user_reaction: userReaction };
      }));
    } catch {}
  };

  const submitComment = async () => {
    const body = commentText.trim();
    if (!body || sendingComment) return;
    setSendingComment(true);
    try {
      const res = await fetch(`${API_BASE}/api/announcements/${ann.id}/comments`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      });
      if (res.ok) {
        const d = await res.json();
        setComments((cs) => [...cs, {
          id: d.id,
          announcement_id: ann.id,
          author_name: d.author_name,
          body: d.body,
          created_at: d.created_at,
          reaction_counts: {},
          user_reaction: null,
          can_delete: true,
        }]);
        setAnn({ ...ann, comment_count: (ann.comment_count || 0) + 1 });
        setCommentText("");
      }
    } finally {
      setSendingComment(false);
    }
  };

  const deleteComment = async (comment) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/announcements/${ann.id}/comments/${comment.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (res.ok) {
        setComments((cs) => cs.filter((c) => c.id !== comment.id));
        setAnn({ ...ann, comment_count: Math.max(0, (ann.comment_count || 0) - 1) });
      }
    } catch {}
  };

  const patchAnn = async (patch) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/announcements/${ann.id}`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        setAnn({ ...ann, ...patch });
        if (onChanged) onChanged({ ...ann, ...patch });
      }
    } catch {}
  };

  const deleteAnn = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/announcements/${ann.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (res.ok && onDeleted) onDeleted(ann.id);
    } catch {}
  };

  const catColor = ann.category === "urgent" ? AMBER
                  : ann.category === "kudos"  ? NEON
                  : BLUE;

  return (
    <div style={{
      border: ann.pinned ? `1px solid ${NEON}55` : "0.5px solid #1c1c1c",
      borderRadius: 12,
      background: "#0d0d0d",
      padding: 14,
      marginBottom: 12,
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          background: "rgba(55,138,221,0.12)", color: BLUE,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 600, flexShrink: 0,
        }}>
          {initials(ann.author_name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "#fff", fontWeight: 500 }}>{ann.author_name || "Admin"}</span>
            <span style={{ fontSize: 11, color: "#555" }}>·</span>
            <span style={{ fontSize: 11, color: "#777" }}>{relTime(ann.created_at)}</span>
            {ann.pinned && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 3,
                fontSize: 10, padding: "1px 6px", borderRadius: 4,
                background: "rgba(0,255,153,0.1)", color: NEON,
                border: `0.5px solid ${NEON}55`,
              }}>
                <Pin size={9} /> Pinned
              </span>
            )}
            {ann.status === "scheduled" && (
              <span style={{
                fontSize: 10, padding: "1px 6px", borderRadius: 4,
                background: "rgba(239,159,39,0.1)", color: AMBER,
              }}>
                Scheduled
              </span>
            )}
            {ann.status === "hidden" && (
              <span style={{
                fontSize: 10, padding: "1px 6px", borderRadius: 4,
                background: "rgba(255,80,80,0.1)", color: "#e66",
              }}>
                Hidden
              </span>
            )}
          </div>
          {ann.target_audience && ann.target_audience !== "company" && (
            <div style={{ fontSize: 10, color: "#555", marginTop: 2 }}>
              Target: {ann.target_audience}
            </div>
          )}
        </div>
        {ann.can_moderate && (
          <AdminMenu ann={ann} onPatch={patchAnn} onDelete={deleteAnn} />
        )}
      </div>

      {/* Title + body */}
      <div style={{ marginTop: 10 }}>
        <h3 style={{ fontSize: 15, color: "#fff", fontWeight: 600, margin: "0 0 6px 0" }}>
          {ann.title}
        </h3>
        {ann.body && (
          <p style={{ fontSize: 14, color: "#bbb", lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap" }}>
            {ann.body}
          </p>
        )}
        {ann.category && (
          <span style={{
            display: "inline-block",
            marginTop: 8,
            fontSize: 10,
            padding: "2px 8px",
            borderRadius: 4,
            background: `${catColor}1a`,
            color: catColor,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}>
            {ann.category}
          </span>
        )}
      </div>

      {/* Attachment */}
      {ann.attachment_url && (
        <div style={{
          marginTop: 12,
          padding: 10,
          borderRadius: 8,
          background: "rgba(255,255,255,0.02)",
          border: "0.5px solid #1c1c1c",
        }}>
          {isImageType(ann.attachment_type, ann.attachment_name) ? (
            <a href={absoluteUrl(ann.attachment_url)} target="_blank" rel="noreferrer">
              <img
                src={absoluteUrl(ann.attachment_url)}
                alt={ann.attachment_name || "attachment"}
                style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 6, display: "block" }}
              />
            </a>
          ) : (
            <a
              href={absoluteUrl(ann.attachment_url)}
              target="_blank"
              rel="noreferrer"
              download={ann.attachment_name}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                color: BLUE, textDecoration: "none", fontSize: 12,
              }}
            >
              <FileText size={16} />
              <span style={{ flex: 1, color: "#ddd" }}>{ann.attachment_name || "Download"}</span>
              <Download size={14} />
            </a>
          )}
        </div>
      )}

      {/* Reactions */}
      {ann.allow_reactions && (
        <div style={{ marginTop: 12 }}>
          <ReactionBar
            counts={ann.reaction_counts}
            userReaction={ann.user_reaction}
            onToggle={reactToAnn}
          />
        </div>
      )}

      {/* Comments toggle */}
      {ann.allow_comments && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "0.5px solid #1a1a1a" }}>
          <button
            onClick={toggleComments}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              fontSize: 11, color: "#888",
              background: "transparent", border: "none", cursor: "pointer",
              padding: 0,
            }}
          >
            <MessageSquare size={12} />
            {ann.comment_count || 0} {(ann.comment_count === 1) ? "comment" : "comments"}
          </button>

          {commentsOpen && (
            <div style={{ marginTop: 8 }}>
              {comments.length === 0 && (
                <p style={{ fontSize: 11, color: "#555", padding: "8px 0", margin: 0 }}>
                  No comments yet. Be the first.
                </p>
              )}
              {comments.map((c) => (
                <CommentItem
                  key={c.id}
                  comment={c}
                  onReact={(emoji) => reactToComment(c, emoji)}
                  onDelete={() => deleteComment(c)}
                />
              ))}

              <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") submitComment(); }}
                  maxLength={500}
                  placeholder="Write a comment…"
                  style={{
                    flex: 1,
                    background: "#0a0a0a",
                    border: "0.5px solid #2a2a2a",
                    borderRadius: 8,
                    padding: "8px 10px",
                    fontSize: 12,
                    color: "#fff",
                    outline: "none",
                  }}
                />
                <button
                  onClick={submitComment}
                  disabled={sendingComment || !commentText.trim()}
                  style={{
                    background: "rgba(0,255,153,0.15)",
                    border: `0.5px solid ${NEON}55`,
                    color: NEON,
                    borderRadius: 8,
                    padding: "0 12px",
                    fontSize: 12,
                    cursor: sendingComment ? "not-allowed" : "pointer",
                    opacity: sendingComment || !commentText.trim() ? 0.5 : 1,
                  }}
                >
                  <Send size={12} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
